import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { Inbox, Hammer, PackageCheck, PartyPopper, CheckCircle2, Loader } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const STATS = [
  { id: "aspirasi", icon: Inbox, value: 128, suffix: "", label: "Aspirasi Warga Diterima", note: "Sejak Januari 2026" },
  { id: "perbaikan", icon: Hammer, value: 46, suffix: "", label: "Perbaikan Dieksekusi", note: "Infrastruktur & fasilitas umum" },
  { id: "bantuan", icon: PackageCheck, value: 312, suffix: "", label: "Paket Bantuan Tersalurkan", note: "Tepat sasaran ke warga" },
  { id: "kegiatan", icon: PartyPopper, value: 27, suffix: "", label: "Kegiatan Komunitas Difasilitasi", note: "Pemuda, wota, otaku, mancing" },
];

const FIXES = [
  { title: "Lampu penerangan Pos Ronda Dusun 3", date: "Selesai · 12 Juni 2026", done: true },
  { title: "Perbaikan jalan berlubang akses sawah Dusun 1", date: "Selesai · 28 Mei 2026", done: true },
  { title: "Sound system untuk kegiatan pemuda balai desa", date: "Selesai · 17 Mei 2026", done: true },
  { title: "Normalisasi saluran irigasi Dusun 2", date: "Dalam Proses · target Agustus 2026", done: false },
];

const Counter = ({ value, suffix, testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setN(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} data-testid={testid}>
      {n}
      {suffix}
    </span>
  );
};

export const TransparencyStats = () => (
  <section id="transparansi" data-testid="transparency-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <SectionHeading
        bab="Transparansi"
        label="Kerja Terukur"
        title="Cianjay dalam Angka"
        sub="Setiap aspirasi dicatat, setiap janji dieksekusi. Inilah jejak kerja yang bisa warga periksa kapan saja."
        testid="transparency"
      />

      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {STATS.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={`stat-card-${s.id}`}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -6 }}
            className="rounded-3xl border border-line bg-surface p-6 sm:p-7 shadow-[0_10px_30px_rgba(30,58,43,0.06)]"
          >
            <span className="grid place-items-center w-11 h-11 rounded-2xl bg-badge text-terra mb-6">
              <s.icon size={20} />
            </span>
            <p className="font-serif font-bold text-4xl sm:text-5xl tracking-tight text-forest dark:text-gold">
              <Counter value={s.value} suffix={s.suffix} testid={`stat-value-${s.id}`} />
            </p>
            <p className="mt-2.5 text-sm font-semibold leading-snug">{s.label}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-soft">{s.note}</p>
          </motion.div>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-6 rounded-3xl border border-line bg-[#1E3A2B] text-[#F4EFE6] p-6 sm:p-9" data-testid="transparency-fixes">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h3 className="font-serif text-xl sm:text-2xl font-semibold">Eksekusi Terbaru di Lapangan</h3>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Diperbarui Juli 2026</span>
          </div>
          <ul className="divide-y divide-white/10">
            {FIXES.map((f, i) => (
              <li key={i} className="flex items-center gap-4 py-4" data-testid={`fix-item-${i}`}>
                <span className={`grid place-items-center w-9 h-9 rounded-full shrink-0 ${f.done ? "bg-gold/20 text-gold" : "bg-terra/25 text-amber2"}`}>
                  {f.done ? <CheckCircle2 size={17} /> : <Loader size={17} className="animate-spin-slow" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm sm:text-base font-medium">{f.title}</span>
                  <span className="block text-[11px] text-[#F4EFE6]/55 mt-0.5">{f.date}</span>
                </span>
                <span className={`shrink-0 rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] ${
                  f.done ? "bg-gold/15 text-gold" : "bg-terra/20 text-amber2"
                }`}>
                  {f.done ? "Selesai" : "Proses"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);
